import React, { useEffect, useRef, useState } from "react";
import { LocaleCurrency } from "helpers/Locale";

import "elements/register/register.scss";
import Sheet from "components/sheet/Sheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartPlus } from "@fortawesome/pro-duotone-svg-icons";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { range } from "lodash";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { getQuantityAvailableForSelectedRegister } from "helpers/registerInventory";
import classNames from "classnames";
import RightClickMenu from "components/rightClickMenu/RightClickMenu";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import DataTable from "components/dataTable/DataTable";
import { useRegisterContext } from "./RegisterContext";
import ReactDOM from "react-dom";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import {
  loadCart,
  loadTableCart,
  postCartVariants,
  putLineItemDynamicComboSelections,
  setActiveTable,
} from "redux/actions/cart";
import { ICart, ICartLineItem, IDiscountLines, IUpdatedDynamicComboSelection } from "redux/reducers/models/cart";
import { IModifierGroup } from "api/rpc/facilityAdmin/modifierGroups";
import SelectModifierSheet from "components/selectModifierSheet/SelectModifierSheet";
import axios, { CancelToken } from "axios";
import { GetModifierGroup } from "api/rpc/2024-04/facilityAdmin/product/modifier/modifierGroup";
import { IAPIResponse, StatusCode } from "api/protocols";
import { getStoredProductFolder, upsertStoredProductFolder } from "helpers/productFoldersStorage";
import { IFolder } from "api/rpc/2022-09/facilityAdmin/product/folder";
import Spin from "components/spin/spin";

import { StateType } from "../../teesheet/newTeeSheetDetails/NewTeeSheetDetails";
import { DeleteLineItemToCart, PostLineItemToCart, PutLineItemToCart } from "api/rpc/2024-04/facilityAdmin/order/cart/lineItem";
import { PostDiscountLine } from "api/rpc/2024-04/facilityAdmin/order/cart/discountLine";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import SelectDynamicComboSheet from "components/selectDynamicComboSheet/SelectDynamicComboSheet";
import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import { calculateHexColorBrightness } from "helpers/Helpers";

interface IProps {
  products: IProduct[];
  useGridPositions: boolean;
  disabled?: boolean;
  cart: ICart;
  selectedFolder: IFolder;
  //Update state function for state cart (Tee Sheet Details)
  updateStateCart?: <T>(newState: Partial<T>, type: StateType) => void;

  /**Message that displays when products are loading */
  productsLoadingMessage: string;

  /**Meal course id selected on Table Service */
  selectedCourseId?: number;

  //Update register tab
  updateTabCart?: (updatedCart: ICart) => void;

  onProductAdded?: (payload: { product: IProduct; sourceElement: HTMLElement | null }) => void;
  optimisticCartAdd?: boolean;
  onOptimisticPendingCartAddCountChange?: (count: number) => void;

  /**Callback that overrides default behaviour on variant click */
  onVariantClick?: (product: IProduct) => void;
}

interface IModifierState {
  sheetOpen: boolean;
  allModifierGroups: IModifierGroup[];
  selectedModifierVariants: { [key: string]: IVariant[] };
}

interface IDynamicComboState {
  sheetOpen: boolean;
}

export default function Products(props: IProps) {
  const GRID_COL = 6;
  const [gridRows, setGridRows] = useState<number>(0);
  const {
    cart,
    disabled,
    products,
    useGridPositions,
    selectedFolder,
    productsLoadingMessage,
    updateStateCart,
    updateTabCart,
    selectedCourseId,
    onProductAdded,
    optimisticCartAdd,
    onOptimisticPendingCartAddCountChange,
    onVariantClick,
  } = props;

  const { facilityStore } = useAppSelector(store => store);

  const { state, updateModal, closeModal } = useModal({ product: undefined as IProduct });
  const [variantSheetOpen, setVariantSheetOpen] = useState<boolean>(false);
  const [selectedVariantQuantities, setSelectedVariantQuantities] = useState<Record<number, number>>({});
  const [addingMultiselectVariants, setAddingMultiselectVariants] = useState(false);
  const {
    registerState,
    updateRegisterState,
    loadingVariants,
    addLoadingVariant,
    removeLoadingVariant,
    addLineItemUpdating,
    removeLineItemUpdating,
  } = useRegisterContext();
  const dispatch = useAppDispatch();
  const { cartStore } = useAppSelector(store => store);
  const { t } = useTranslation();
  const [modifierState, setModifierState] = useState<IModifierState>({
    sheetOpen: false,
    allModifierGroups: [],
    selectedModifierVariants: null,
  });

  const [dynamicComboState, setDynamicComboState] = useState<IDynamicComboState>({
    sheetOpen: false,
  });
  const productSourceRef = useRef<HTMLElement | null>(null);
  const optimisticAddQueueRef = useRef<Array<{ variant: IVariant; product: IProduct }>>([]);
  const optimisticAddInFlightRef = useRef(false);

  const isVariantMultiselectEnabled =
    Boolean(registerState?.selectedModifierProduct?.variant_multiselect) &&
    !registerState?.selectedModifierProduct?.has_modifiers;

  const selectedMultiselectCount = Object.keys(selectedVariantQuantities).length;

  useEffect(() => {
    const positions = products?.map(product => product.folder_position);

    if (positions === undefined || positions.length === 0) {
      setGridRows(0);
    } else {
      const maxPosition = positions.reduce((prev, next) => prev < next ? next : prev);
      const gridRows = Math.ceil(maxPosition / GRID_COL);
      setGridRows(gridRows);
    }
  }, [products]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (registerState?.selectedModifierVariant && registerState?.selectedModifierProduct) {
      if (onVariantClick) {
        void onVariantClick({...registerState?.selectedModifierProduct, variants: [registerState?.selectedModifierVariant]});
        void onVariantModalClose();
        return;
      }
      //Add new modifier item
      void openModifierGroups(registerState?.selectedModifierProduct?.id, source.token);
    } else if (registerState?.modifierLineItem) {
      //Add modifiers to existing parent item
      void openModifierGroups(registerState?.modifierLineItem?.product_id, source.token);
    }
    return () => source.cancel();
  }, [registerState?.selectedModifierVariant, registerState?.modifierLineItem]);

  useEffect(() => {
    if (
      (registerState?.selectedDynamicComboProduct && registerState?.selectedDynamicComboVariant) ||
      registerState?.dynamicComboLineItem
    ) {
      if (onVariantClick) {
        void onVariantClick({...registerState?.selectedDynamicComboProduct, variants: [registerState?.selectedDynamicComboVariant]});
        void onVariantModalClose();
        return;
      }

      setDynamicComboState(prev => ({
        ...prev,
        sheetOpen: true,
      }));
    }
  }, [
    registerState?.selectedDynamicComboProduct,
    registerState?.selectedDynamicComboVariant,
    registerState?.dynamicComboLineItem,
  ]);

  function notifyProductAdded(product: IProduct) {
    if (!onProductAdded) {
      return;
    }
    onProductAdded({ product, sourceElement: productSourceRef.current });
  }

  function onProductClick(product: IProduct, sourceElement: HTMLElement | null) {
    productSourceRef.current = sourceElement;
    const variants = product?.variants?.filter(variant => variant?.facility_access === true);
    // More than one variant -> open Variants Modal
    if (variants?.length > 1) {
      ReactDOM.unstable_batchedUpdates(() => {
        setSelectedVariantQuantities({});
        setVariantSheetOpen(true);
        updateRegisterState({ variants: variants, selectedModifierProduct: product });
      });
    } else {
      if (variants[0]) {
        // Open modifier modal if product has modifiers
        if (product?.has_modifiers) {
          updateRegisterState({ selectedModifierProduct: product, selectedModifierVariant: variants[0] });
          return;
        } else if (product?.type === "Combo" && product?.dynamic_combo) {
          // Open dynamic combo modal. Dynamic combos should only have 1 variant and no modifiers
          updateRegisterState({ selectedDynamicComboProduct: product, selectedDynamicComboVariant: variants[0] });
          return;
        }

        void addVariantToCart(variants[0], product);
      } else {
        dispatch(showError(t("secure.facility.register.register_new.products_new.001")));
      }
    }
  }

  function updateActiveTable(updatedCart: ICart) {
    if (cartStore?.activeTable) {
      const activeTable = { ...cartStore?.activeTable };
      const updatedTable = {
        ...activeTable,
        carts: activeTable?.carts?.map(tableCart => (tableCart?.id === updatedCart?.id ? updatedCart : tableCart)),
      };
      dispatch(setActiveTable(updatedTable));
    }
  }

  function getOptimisticPendingCartAddCount() {
    return loadingVariants?.current?.filter(loadingVariant => !loadingVariant?.parent_id)?.length ?? 0;
  }

  function syncOptimisticPendingCartAddCount() {
    onOptimisticPendingCartAddCountChange?.(getOptimisticPendingCartAddCount());
  }

  function queueOptimisticVariantAdd(variant: IVariant, product: IProduct) {
    //Return to cart page if a product is added when the payment methods page is displayed
    if (registerState?.page === null) {
      updateRegisterState({ page: "cart" });
    }
    void onVariantModalClose();
    void addLoadingVariant(variant, product, undefined, selectedCourseId);
    syncOptimisticPendingCartAddCount();
    optimisticAddQueueRef.current = [...optimisticAddQueueRef.current, { variant, product }];
    notifyProductAdded(product);
    void flushOptimisticAddQueue();
  }

  async function flushOptimisticAddQueue() {
    if (optimisticAddInFlightRef.current || optimisticAddQueueRef.current.length === 0) {
      return;
    }

    optimisticAddInFlightRef.current = true;
    const queuedAdds = optimisticAddQueueRef.current;
    optimisticAddQueueRef.current = [];

    const updatedCart = await dispatch(
      postCartVariants(
        {
          cart_id: cartStore?.cart?.id ?? cart?.id,
          variants: queuedAdds.map(({ variant }) => ({
            variant_id: variant?.id,
            quantity: 1,
            kitchen_meal_id: selectedCourseId,
          })),
        },
        false,
      ),
    );

    queuedAdds.forEach(({ variant }) => removeLoadingVariant(variant?.id));
    syncOptimisticPendingCartAddCount();

    if (!updatedCart) {
      dispatch(showError(t("secure.facility.register.register_new.products_new.004")));
    } else {
      if (updateTabCart) {
        void updateTabCart(updatedCart);
      }
      if (cartStore?.activeTable) {
        void updateActiveTable(updatedCart);
      }
    }

    optimisticAddInFlightRef.current = false;
    if (optimisticAddQueueRef.current.length > 0) {
      void flushOptimisticAddQueue();
    }
  }

  async function addVariantToCart(variant: IVariant, product: IProduct) {
    if (onVariantClick) {
      void onVariantClick({...product, variants: [variant]});
      void onVariantModalClose();
      return;
    }
    const stackLineItems = facilityStore?.teeSheetSettings?.line_items_stack;
    if (optimisticCartAdd && !updateStateCart && !stackLineItems && !product?.dynamic_price) {
      queueOptimisticVariantAdd(variant, product);
      return;
    }

    //Return to cart page if a product is added when the payment methods page is displayed
    if (registerState?.page === null) {
      updateRegisterState({ page: "cart" });
    }
    void onVariantModalClose();
    let foundLineItem: ICartLineItem = null;
    if (stackLineItems) {
      foundLineItem = cart?.line_items?.find(lineItem => lineItem?.variant_id === variant?.id && !lineItem?.parent_id);
      if (foundLineItem) {
        void addLineItemUpdating(foundLineItem?.id, "edit");
      } else {
        void addLoadingVariant(variant, product, undefined, selectedCourseId);
      }
    } else {
      void addLoadingVariant(variant, product, undefined, selectedCourseId);
    }

    //State cart
    if (updateStateCart) {
      const postVariantRes = await PostLineItemToCart(
        { cart_id: cart?.id, variants: [{ variant_id: variant?.id, quantity: 1, parent_id: null }] },
        false,
      );

      if (stackLineItems) {
        if (foundLineItem) {
          void removeLineItemUpdating(foundLineItem?.id);
        } else {
          void removeLoadingVariant(variant?.id);
        }
      } else {
        void removeLoadingVariant(variant?.id);
      }

      if (postVariantRes?.status !== StatusCode?.OK) {
        dispatch(showError(t("secure.facility.register.register_new.products_new.002")));
      } else {
        if (updateTabCart) {
          void updateTabCart(postVariantRes?.data);
        }

        void updateStateCart({ cart: postVariantRes?.data }, StateType.CheckInState);
        dispatch(showSuccess(t("secure.facility.register.register_new.products_new.003"), 2000));

        if (product?.dynamic_price && postVariantRes?.data?.line_items) {
          const addedLineItem = postVariantRes.data.line_items.find(
            (li: ICartLineItem) => li.variant_id === variant.id && !li.parent_id,
          );
          if (addedLineItem) {
            updateRegisterState({ dynamicPriceLineItem: addedLineItem });
          }
        }
      }
    } else {
      //Redux Cart
      const updatedCart = await dispatch(
        postCartVariants(
          {
            cart_id: cart?.id,
            variants: [{ variant_id: variant?.id, quantity: 1, parent_id: null, kitchen_meal_id: selectedCourseId }],
          },
          false,
        ),
      );

      if (stackLineItems) {
        if (foundLineItem) {
          void removeLineItemUpdating(foundLineItem?.id);
        } else {
          void removeLoadingVariant(variant?.id);
        }
      } else {
        void removeLoadingVariant(variant?.id);
      }
      if (!updatedCart) {
        dispatch(showError(t("secure.facility.register.register_new.products_new.004")));
      } else {
        notifyProductAdded(product);
        if (updateTabCart) {
          void updateTabCart(updatedCart);
        }
        if (cartStore?.activeTable) {
          void updateActiveTable(updatedCart);
        }
      }

      if (updatedCart && product?.dynamic_price && updatedCart?.line_items) {
        const addedLineItem = updatedCart.line_items.find(
          (li: ICartLineItem) => li.variant_id === variant.id && !li.parent_id,
        );
        if (addedLineItem) {
          updateRegisterState({ dynamicPriceLineItem: addedLineItem });
        }
      }
    }
  }

  async function openModifierGroups(productId: number, cancelToken?: CancelToken) {
    ReactDOM.unstable_batchedUpdates(() => {
      setVariantSheetOpen(false);
      setModifierState(prevState => ({ ...prevState, sheetOpen: true }));
    });

    //Get folder in IndexedDB
    const storedProductFolder = await getStoredProductFolder(selectedFolder?.id);
    // Get selected product in IndexedDB
    const storedProduct = storedProductFolder?.products?.find(product => product?.id === productId);
    let modifierGroups: Array<IModifierGroup>;
    let loadModifierGroups = false;

    if (storedProductFolder) {
      if (storedProduct && storedProduct?.modifier_groups && storedProduct?.modifier_groups?.length > 0) {
        //Modifier group found in IndexedDB
        modifierGroups = storedProduct?.modifier_groups;
      } else {
        loadModifierGroups = true;
      }
    } else {
      loadModifierGroups = true;
    }

    // Modifier groups not found, load them
    if (loadModifierGroups) {
      const modifierGroupResponse = await GetModifierGroup(
        { product_id: productId, extended: true },
        false,
        cancelToken,
      );
      if (modifierGroupResponse.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("secure.facility.register.register_new.products_new.005")));
        return;
      }
      modifierGroups = modifierGroupResponse?.data;
      if (storedProductFolder && storedProduct) {
        const updatedProducts = storedProductFolder?.products?.map(product => {
          if (product?.id === storedProduct?.id) {
            return {
              ...storedProduct,
              modifier_groups: modifierGroups,
            };
          }
          return product;
        });
        await upsertStoredProductFolder({ ...storedProductFolder, products: updatedProducts });
      }
    }

    const selectedModifiers: { [key: string]: IVariant[] } = {};
    const existingModifierLineItems =
      registerState?.modifierLineItem && cart?.line_items
        ? cart.line_items.filter(lineItem => lineItem.parent_id === registerState.modifierLineItem.id)
        : [];

    // Preselect existing modifiers when editing, otherwise defaults
    modifierGroups?.forEach((modifier: IModifierGroup) => {
      const groupKey = String(modifier.id);
      selectedModifiers[groupKey] = [];
      (modifier.variants as IVariant[] | undefined)?.forEach((variant: IVariant) => {
        const existingModifier = existingModifierLineItems.find(lineItem => lineItem.variant_id === variant.id);
        if (existingModifier) {
          selectedModifiers[groupKey].push(variant);
        } else if (!registerState?.modifierLineItem && variant.modifier_group?.default) {
          selectedModifiers[groupKey].push(variant);
        }
      });
    });

    setModifierState(prevState => ({
      ...prevState,
      selectedModifierVariants: selectedModifiers,
      allModifierGroups: modifierGroups,
    }));
  }

  function checkModifiers(variant: IVariant, product: IProduct) {
    // Open modifier modal if product has modifiers
    if (product?.has_modifiers) {
      updateRegisterState({ selectedModifierProduct: product, selectedModifierVariant: variant });
    } else {
      void addVariantToCart(variant, product);
    }
  }

  function toggleVariantMultiselect(variant: IVariant) {
    if (variant?.meta?.cannot_be_sold) {
      return;
    }

    setSelectedVariantQuantities(prev => {
      if (prev[variant.id] != null) {
        const next = { ...prev };
        delete next[variant.id];
        return next;
      }
      return { ...prev, [variant.id]: 1 };
    });
  }

  function updateSelectedVariantQuantity(variantId: number, delta: number) {
    setSelectedVariantQuantities(prev => {
      const current = prev[variantId];
      if (current == null) {
        return prev;
      }
      const nextQuantity = current + delta;
      if (nextQuantity <= 0) {
        const next = { ...prev };
        delete next[variantId];
        return next;
      }
      return { ...prev, [variantId]: nextQuantity };
    });
  }

  function onVariantModalClose() {
    ReactDOM.unstable_batchedUpdates(() => {
      setVariantSheetOpen(false);
      setSelectedVariantQuantities({});
      setAddingMultiselectVariants(false);
      updateRegisterState({ variants: null, selectedModifierProduct: null, selectedModifierVariant: null });
    });
  }

  async function addSelectedVariantsToCart() {
    const product = registerState?.selectedModifierProduct;
    const availableVariants = registerState?.variants ?? [];
    if (!product || selectedMultiselectCount === 0) {
      return;
    }

    const selections = Object.entries(selectedVariantQuantities)
      .map(([variantId, quantity]) => {
        const variant = availableVariants.find(item => item.id === Number(variantId));
        if (!variant || quantity <= 0) {
          return null;
        }
        return { variant, quantity };
      })
      .filter((selection): selection is { variant: IVariant; quantity: number } => selection != null);

    if (selections.length === 0) {
      return;
    }

    if (registerState?.page === null) {
      updateRegisterState({ page: "cart" });
    }

    setAddingMultiselectVariants(true);
    selections.forEach(({ variant }) => {
      void addLoadingVariant(variant, product, undefined, selectedCourseId);
    });

    const variantsPayload = selections.map(({ variant, quantity }) => ({
      variant_id: variant.id,
      quantity,
      parent_id: null,
      kitchen_meal_id: selectedCourseId,
    }));

    const clearLoading = () => {
      selections.forEach(({ variant }) => removeLoadingVariant(variant.id));
    };

    try {
      if (updateStateCart) {
        const postVariantRes = await PostLineItemToCart(
          { cart_id: cart?.id, variants: variantsPayload },
          false,
        );
        clearLoading();

        if (postVariantRes?.status !== StatusCode?.OK) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.002")));
        } else {
          if (updateTabCart) {
            void updateTabCart(postVariantRes?.data);
          }
          void updateStateCart({ cart: postVariantRes?.data }, StateType.CheckInState);
          dispatch(showSuccess(t("secure.facility.register.register_new.products_new.003"), 2000));
          void onVariantModalClose();
        }
      } else {
        const updatedCart = await dispatch(
          postCartVariants(
            {
              cart_id: cartStore?.cart?.id ?? cart?.id,
              variants: variantsPayload,
            },
            false,
          ),
        );
        clearLoading();

        if (!updatedCart) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.004")));
        } else {
          notifyProductAdded(product);
          if (updateTabCart) {
            void updateTabCart(updatedCart);
          }
          if (cartStore?.activeTable) {
            void updateActiveTable(updatedCart);
          }
          void onVariantModalClose();
        }
      }
    } finally {
      setAddingMultiselectVariants(false);
    }
  }

  async function onModifiersOk() {
    //Return to cart page if a product is added when the payment methods page is displayed
    if (registerState?.page === null) {
      updateRegisterState({ page: "cart" });
    }

    //Modify new line item
    if (registerState?.selectedModifierProduct && registerState?.selectedModifierVariant) {
      const selectedVariant = { ...registerState?.selectedModifierVariant };
      const selectedProduct = { ...registerState?.selectedModifierProduct };
      const modifiersToAdd: Array<{ variant_id: number; quantity: number }> = [];
      const modifierVariants: Array<IVariant> = [];

      for (const key in modifierState.selectedModifierVariants) {
        modifierState.selectedModifierVariants[key]?.forEach(variant => {
          modifiersToAdd.push({ variant_id: variant?.id, quantity: Math.max(1, Number(variant?.quantity) || 1) });
          modifierVariants.push({ ...variant, quantity: Math.max(1, Number(variant?.quantity) || 1) });
        });
      }

      const variantParam = {
        cart_id: cartStore?.cart?.id,
        variant_id: selectedVariant?.id,
        quantity: 1,
        modifiers: modifiersToAdd,
        kitchen_meal_id: selectedCourseId,
      };

      void addLoadingVariant(selectedVariant, selectedProduct, modifierVariants, selectedCourseId);
      void handleCloseModifierGroups();
      updateRegisterState({ selectedModifierProduct: null, selectedModifierVariant: null });
      //State cart
      if (updateStateCart) {
        const lineItemRes = await PostLineItemToCart({ cart_id: cart?.id, variants: [variantParam] }, false);
        void removeLoadingVariant(selectedVariant?.id);
        if (lineItemRes?.status !== StatusCode?.OK) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.006")));
        } else {
          void updateStateCart({ cart: lineItemRes?.data }, StateType.CheckInState);
          if (updateTabCart) {
            void updateTabCart(lineItemRes?.data);
          }

          dispatch(showSuccess(t("secure.facility.register.register_new.products_new.007"), 2000));

          if (selectedProduct?.dynamic_price && lineItemRes?.data?.line_items) {
            const addedLineItem = lineItemRes.data.line_items.find(
              (li: ICartLineItem) => li.variant_id === selectedVariant.id && !li.parent_id,
            );
            if (addedLineItem) {
              updateRegisterState({ dynamicPriceLineItem: addedLineItem });
            }
          }
        }
      } else {
        //Redux cart
        const updatedCart = await dispatch(
          postCartVariants({ cart_id: cartStore?.cart?.id, variants: [variantParam] }, false),
        );
        void removeLoadingVariant(selectedVariant?.id);

        if (!updatedCart) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.008")));
          updateRegisterState({ selectedModifierProduct: null, selectedModifierVariant: null });
        } else {
          notifyProductAdded(selectedProduct);
          if (cartStore?.activeTable) {
            void updateActiveTable(updatedCart);
          }
          if (updateTabCart) {
            void updateTabCart(updatedCart);
          }
        }

        if (updatedCart && selectedProduct?.dynamic_price && updatedCart?.line_items) {
          const addedLineItem = updatedCart.line_items.find(
            (li: ICartLineItem) => li.variant_id === selectedVariant.id && !li.parent_id,
          );
          if (addedLineItem) {
            updateRegisterState({ dynamicPriceLineItem: addedLineItem });
          }
        }
      }
    } else if (registerState?.modifierLineItem) {
      // Modify existing line item modifiers: sync adds and removes
      const lineItem = { ...registerState?.modifierLineItem };
      const existingModifierLineItems =
        cart?.line_items?.filter(cartLineItem => cartLineItem.parent_id === lineItem.id) ?? [];
      const selectedVariants: IVariant[] = [];

      for (const key in modifierState.selectedModifierVariants) {
        modifierState.selectedModifierVariants[key]?.forEach(variant => {
          selectedVariants.push(variant);
        });
      }

      const selectedVariantIds = new Set(selectedVariants.map(variant => variant.id));
      const existingVariantIds = new Set(existingModifierLineItems.map(cartLineItem => cartLineItem.variant_id));

      const modifiersToRemove = existingModifierLineItems.filter(
        cartLineItem => !selectedVariantIds.has(cartLineItem.variant_id),
      );
      const modifiersToAdd = selectedVariants
        .filter(variant => !existingVariantIds.has(variant.id))
        .map(variant => ({
          variant_id: variant.id,
          quantity: Math.max(1, Number(variant.quantity) || 1),
          parent_id: lineItem.id,
        }));
      const modifierVariantsToAdd = selectedVariants
        .filter(variant => !existingVariantIds.has(variant.id))
        .map(variant => ({ ...variant, quantity: Math.max(1, Number(variant.quantity) || 1), parent_id: lineItem.id }));

      let updatedCart: ICart;

      // Remove deselected modifiers first
      for (const modifierLineItem of modifiersToRemove) {
        const deleteRes = await DeleteLineItemToCart({ id: modifierLineItem.id }, false);
        if (deleteRes?.status !== StatusCode.OK) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.009")));
          return;
        }
        updatedCart = deleteRes.data;
      }

      for (const existingModifier of existingModifierLineItems) {
        const selectedVariant = selectedVariants.find(variant => variant.id === existingModifier.variant_id);
        const nextQuantity = Math.max(1, Number(selectedVariant?.quantity) || 1);
        if (selectedVariant && existingModifier.quantity !== nextQuantity) {
          const putRes = await PutLineItemToCart({ id: existingModifier.id, quantity: nextQuantity }, false);
          if (putRes?.status !== StatusCode.OK) {
            dispatch(showError(t("secure.facility.register.register_new.products_new.009")));
            return;
          }
          updatedCart = putRes.data;
        }
      }

      // Add newly selected modifiers
      if (modifiersToAdd.length > 0) {
        modifierVariantsToAdd.forEach(variant => {
          void addLoadingVariant(variant);
        });

        if (updateStateCart) {
          const lineItemRes = await PostLineItemToCart({ cart_id: cart?.id, variants: modifiersToAdd }, false);
          modifierVariantsToAdd.forEach(variant => {
            void removeLoadingVariant(variant.id);
          });
          if (lineItemRes?.status !== StatusCode?.OK) {
            dispatch(showError(t("secure.facility.register.register_new.products_new.009")));
            return;
          }
          updatedCart = lineItemRes.data;
        } else {
          updatedCart = await dispatch(
            postCartVariants({ cart_id: cartStore?.cart?.id, variants: modifiersToAdd }, false),
          );
          modifierVariantsToAdd.forEach(variant => {
            void removeLoadingVariant(variant.id);
          });
          if (!updatedCart) {
            dispatch(showError(t("secure.facility.register.register_new.products_new.011")));
            return;
          }
        }
      }

      if (updateStateCart) {
        void updateStateCart({ cart: updatedCart }, StateType.CheckInState);
        if (updateTabCart) {
          void updateTabCart(updatedCart);
        }
        dispatch(showSuccess(t("secure.facility.register.register_new.products_new.010"), 2000));
      } else {
        void dispatch(loadTableCart({ tableCart: updatedCart }, false));
        if (cartStore?.activeTable) {
          void updateActiveTable(updatedCart);
        }
        if (updateTabCart) {
          void updateTabCart(updatedCart);
        }
      }

      if (updatedCart && modifiersToAdd.length > 0) {
        void applyDiscountsToModifiers(lineItem, updatedCart);
      }

      void handleCloseModifierGroups();
    }
  }

  function handleCloseModifierGroups() {
    ReactDOM.unstable_batchedUpdates(() => {
      updateRegisterState({ selectedModifierProduct: null, selectedModifierVariant: null, modifierLineItem: null });
      setModifierState(prevState => ({
        ...prevState,
        sheetOpen: false,
        allModifierGroups: [],
        selectedModifierVariants: null,
      }));
    });
  }

  function addModifierVariant(modifierGroup: IModifierGroup, variant: IVariant, selected: boolean) {
    const selectedModifiers = { ...modifierState?.selectedModifierVariants };
    const groupKey = modifierGroup.id.toString();
    const isRequired = modifierGroup.required || !!modifierGroup.modifier?.required;

    if (modifierGroup.multi_select) {
      if (selected && selectedModifiers[groupKey]?.length >= 1) {
        if (isRequired && selectedModifiers[groupKey].length === 1) {
          return;
        }
        selectedModifiers[groupKey] = selectedModifiers[groupKey].filter(filtered => filtered.id !== variant.id);
      } else {
        selectedModifiers[groupKey] = [...(selectedModifiers[groupKey] ?? []), { ...variant, quantity: 1 }];
      }
    } else if (selected) {
      if (!isRequired) {
        selectedModifiers[groupKey] = [];
      }
    } else {
      selectedModifiers[groupKey] = [{ ...variant, quantity: 1 }];
    }

    setModifierState(prevState => ({ ...prevState, selectedModifierVariants: selectedModifiers }));
  }

  function changeModifierQuantity(modifierGroup: IModifierGroup, variant: IVariant, quantity: number) {
    const groupKey = modifierGroup.id.toString();
    const selectedModifiers = { ...modifierState?.selectedModifierVariants };
    selectedModifiers[groupKey] = (selectedModifiers[groupKey] ?? []).map(selectedVariant =>
      selectedVariant.id === variant.id ? { ...selectedVariant, quantity } : selectedVariant,
    );
    setModifierState(prevState => ({ ...prevState, selectedModifierVariants: selectedModifiers }));
  }

  function clearModifierSelections() {
    if (!modifierState?.selectedModifierVariants) {
      return;
    }

    const clearedSelections = Object.keys(modifierState.selectedModifierVariants).reduce(
      (selections, key) => {
        selections[key] = [];
        return selections;
      },
      {} as { [key: string]: IVariant[] },
    );

    setModifierState(prevState => ({ ...prevState, selectedModifierVariants: clearedSelections }));
  }

  function disableSelectingModifiers() {
    //Disable checks for adding modifiers to existing line item
    if (registerState?.modifierLineItem) {
      const lineItem = { ...registerState?.modifierLineItem };
      if (lineItem && cart?.line_items && modifierState?.allModifierGroups?.length > 0) {
        const requiredModifierGroups = modifierState?.allModifierGroups?.filter(
          modifierGroup => modifierGroup.required || !!modifierGroup.modifier?.required,
        );

        const lineItemModifiers = cart?.line_items.filter(cartLineItem => {
          return cartLineItem.id !== lineItem.id && lineItem.id === cartLineItem.parent_id;
        });

        const modalRequiredModifierGroups: IModifierGroup[] = requiredModifierGroups.filter(modifierGroup => {
          return !modifierGroup.variants?.some(variant =>
            lineItemModifiers.some(lineItem => lineItem.variant_id === variant.id),
          );
        });

        const allRequiredModifiersSelected = modalRequiredModifierGroups.every(modifierGroup => {
          return modifierState?.selectedModifierVariants[modifierGroup.id.toString()]?.length > 0;
        });

        return !allRequiredModifiersSelected;
      } else {
        return true;
      }
    } else {
      // Disable checks for adding modifiers to new line item
      if (registerState?.selectedModifierVariant && modifierState?.allModifierGroups?.length > 0) {
        const requiredModifierGroups = modifierState?.allModifierGroups?.filter(
          modifierGroup => modifierGroup.required || !!modifierGroup.modifier?.required,
        );
        const allRequiredModifiersSelected = requiredModifierGroups?.every(
          modifierGroup => modifierState?.selectedModifierVariants[modifierGroup.id.toString()]?.length > 0,
        );
        return !allRequiredModifiersSelected;
      } else {
        return true;
      }
    }
  }

  async function applyDiscountsToModifiers(lineItem: ICartLineItem, cart: ICart) {
    const cartLineItems = [...cart.line_items]?.filter(cartLineItem => cartLineItem?.parent_id === lineItem?.id);
    //Get the discounts to apply
    const discounts = getDiscountsToApply(lineItem?.id, cart);
    if (cartLineItems.length > 0 && discounts?.length > 0) {
      let responses: Array<Omit<IAPIResponse, "message">>;
      void addLineItemUpdating(lineItem?.id);
      void handleCloseModifierGroups();
      const discountsToApply: Array<{
        discount_id: number;
        cart_id: number;
        cart_line_item_id: number;
        value: number;
        value_type: "percent" | "fixed_amount";
      }> = [];
      cartLineItems?.forEach(cartLineItem => {
        //Check if the line item's parent id matches the selected line item's id
        //Also check if the discount line has already been added to the line item
        if (
          !cart?.discount_lines?.some(discount_line => discount_line?.cart_line_item_id === cartLineItem?.id) &&
          cartLineItem.price > 0
        ) {
          discounts?.forEach(discount => {
            discountsToApply.push({
              discount_id: discount.discount_id,
              cart_id: cartLineItem.cart_id,
              cart_line_item_id: cartLineItem.id,
              value: discount.value ? Number(discount.value) : undefined,
              value_type: discount.value_type,
            });
          });
        }
      });
      if (discountsToApply?.length > 0) {
        responses = await Promise.all(
          discountsToApply?.map(discountParams => PostDiscountLine(discountParams, false)),
        );
        if (responses?.some(res => res?.status !== StatusCode.OK)) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.012")));
        }
      }
      void removeLineItemUpdating(lineItem?.id);

      //State cart
      if (updateStateCart) {
        if (responses) {
          const updatedCart = responses[0]?.data;
          void updateStateCart({ cart: updatedCart }, StateType.CheckInState);
          if (updateTabCart) {
            void updateTabCart(updatedCart as ICart);
          }
        }
      } else {
        //Redux cart
        if (responses) {
          const updatedCart = responses[0]?.data;
          //Set cart in cartStore
          void dispatch(loadTableCart({ tableCart: updatedCart }, false));
          if (cartStore?.activeTable) {
            void updateActiveTable(updatedCart as ICart);
            if (updateTabCart) {
              void updateTabCart(updatedCart as ICart);
            }
          }
        } else {
          void dispatch(loadCart({ id: cartStore?.cart?.id }, true));
          if (updateTabCart) {
            void updateTabCart(cartStore?.cart);
          }
        }
      }
    }
  }

  function getDiscountsToApply(parentId: number, cart: ICart) {
    const cartDiscounts = [...cart.discount_lines];
    let discountsToApply: Array<IDiscountLines> = [];
    //Get discounts that are applied to the parent item and are of type 'percent'
    if (cartDiscounts.length > 0) {
      discountsToApply = cartDiscounts?.filter(
        discount => discount?.cart_line_item_id === parentId && discount?.value_type === "percent",
      );
    }
    return discountsToApply;
  }

  function handleCloseDynamicComboSheet() {
    setDynamicComboState(prev => ({
      ...prev,
      sheetOpen: false,
    }));
    updateRegisterState({
      selectedDynamicComboVariant: null,
      selectedDynamicComboProduct: null,
      dynamicComboLineItem: null,
    });
  }

  async function onDynamicComboOk(dynamic_combo_selections: IUpdatedDynamicComboSelection[]) {
    // Return to cart page if a product is added when the payment methods page is displayed
    if (registerState?.page === null) {
      updateRegisterState({ page: "cart" });
    }
    // Add new line item
    if (registerState?.selectedDynamicComboProduct && registerState?.selectedDynamicComboVariant) {
      const selectedVariant = { ...registerState?.selectedDynamicComboVariant };
      const selectedProduct = { ...registerState?.selectedDynamicComboProduct };

      const variantParam = {
        cart_id: cartStore?.cart?.id,
        variant_id: selectedVariant?.id,
        quantity: 1,
        kitchen_meal_id: selectedCourseId,
        dynamic_combo_selections,
      };

      void addLoadingVariant(selectedVariant, selectedProduct, undefined, selectedCourseId);
      void handleCloseDynamicComboSheet();

      //State cart
      if (updateStateCart) {
        const lineItemRes = await PostLineItemToCart({ cart_id: cart?.id, variants: [variantParam] }, false);
        void removeLoadingVariant(selectedVariant?.id);

        if (lineItemRes?.status !== StatusCode?.OK) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.018")));
        } else {
          void updateStateCart({ cart: lineItemRes?.data }, StateType.CheckInState);
          if (updateTabCart) {
            void updateTabCart(lineItemRes?.data);
          }
          dispatch(showSuccess(t("Products added to this combo successfully."), 2000));

          if (selectedProduct?.dynamic_price && lineItemRes?.data?.line_items) {
            const addedLineItem = lineItemRes.data.line_items.find(
              (li: ICartLineItem) => li.variant_id === selectedVariant.id && !li.parent_id,
            );
            if (addedLineItem) {
              updateRegisterState({ dynamicPriceLineItem: addedLineItem });
            }
          }
        }
      } else {
        //Redux cart
        const updatedCart = await dispatch(
          postCartVariants({ cart_id: cartStore?.cart?.id, variants: [variantParam] }, false),
        );
        void removeLoadingVariant(selectedVariant?.id);
        if (!updatedCart) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.020")));
        } else {
          notifyProductAdded(selectedProduct);
          dispatch(showSuccess(t("Products added to this combo successfully."), 2000));
          if (cartStore?.activeTable) {
            void updateActiveTable(updatedCart);
          }
          if (updateTabCart) {
            void updateTabCart(updatedCart);
          }
        }

        if (updatedCart && selectedProduct?.dynamic_price && updatedCart?.line_items) {
          const addedLineItem = updatedCart.line_items.find(
            (li: ICartLineItem) => li.variant_id === selectedVariant.id && !li.parent_id,
          );
          if (addedLineItem) {
            updateRegisterState({ dynamicPriceLineItem: addedLineItem });
          }
        }
      }
    } else if (registerState?.dynamicComboLineItem) {
      // Modify existing line item
      const lineItem = { ...registerState?.dynamicComboLineItem };

      void addLineItemUpdating(lineItem.id);

      // State cart
      if (updateStateCart) {
        const lineItemRes = await PutLineItemToCart({ id: lineItem.id, dynamic_combo_selections }, false);
        void removeLineItemUpdating(lineItem.id);

        if (lineItemRes?.status !== StatusCode?.OK) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.021")));
        } else {
          void updateStateCart({ cart: lineItemRes?.data }, StateType.CheckInState);
          if (updateTabCart) {
            void updateTabCart(lineItemRes?.data);
          }
          dispatch(showSuccess(t("Products added to this combo successfully."), 2000));
        }
        void handleCloseDynamicComboSheet();
      } else {
        // Redux cart
        const updatedCart = await dispatch(putLineItemDynamicComboSelections(lineItem.id, dynamic_combo_selections));

        void removeLineItemUpdating(lineItem.id);

        if (!updatedCart) {
          dispatch(showError(t("secure.facility.register.register_new.products_new.023")));
        } else {
          dispatch(showSuccess(t("Products added to this combo successfully."), 2000));
          if (cartStore?.activeTable) {
            void updateActiveTable(updatedCart);
          }
          if (updateTabCart) {
            void updateTabCart(updatedCart);
          }
        }
        void handleCloseDynamicComboSheet();
      }
    }
  }

  function renderProducts() {
    if (useGridPositions) {
      return range(1, GRID_COL * gridRows + 1).map((position, index) => {
        const product = products.find(product => product.folder_position === position);

        if (product) {
          return (
            <SingleProductRender
              key={index}
              product={product}
              disabled={disabled}
              t={t}
              onContentClick={onProductClick}
              onMenuClick={product => updateModal({ isOpen: true, product: product })}
              cart={cart}
              teeSheetSettings={facilityStore?.teeSheetSettings}
            />
          );
        } else {
          return <div key={index} className="product_container" />;
        }
      });
    } else {
      return products.map((product, index) => {
        return (
          <SingleProductRender
            key={index}
            product={product}
            disabled={disabled}
            onContentClick={onProductClick}
            t={t}
            onMenuClick={product => updateModal({ isOpen: true, product: product })}
            cart={cart}
            teeSheetSettings={facilityStore?.teeSheetSettings}
          />
        );
      });
    }
  }

  return productsLoadingMessage ? (
    <div style={{ display: "flex", height: "100%" }}>
      <div className="text-center m-auto">
        <div style={{ height: "32px" }}>
          <Spin />
        </div>
        <div>{productsLoadingMessage}</div>
      </div>
    </div>
  ) : (
    <div>
      <div
        className={classNames("ui-register-products", {
          "ui-register-products--folder": useGridPositions,
          "ui-register-products--search": !useGridPositions,
        })}
      >
        {renderProducts()}
      </div>
      {/*Variant modal */}
      <Portal isMounted={variantSheetOpen}>
        <Sheet
          size={"small"}
          title={t("secure.facility.register.register_new.products_new.013")}
          open={variantSheetOpen}
          onCancel={onVariantModalClose}
          onOk={isVariantMultiselectEnabled ? () => void addSelectedVariantsToCart() : undefined}
          okText={t("Add to order")}
          okDisabled={selectedMultiselectCount === 0}
          okLoading={addingMultiselectVariants}
          closable
          stacked
        >
          <div className={classNames("flex flex-col py-2 px-3 mb-4 ui-register-product-variant-title-container")}>
            <span className="ui-register-product-variant-title">
              {registerState?.selectedModifierProduct?.preferred_title
                ? registerState?.selectedModifierProduct?.preferred_title
                : registerState?.selectedModifierProduct?.title}
            </span>
            <span className="ui-register-product-variant-sub_title">{`${registerState?.variants?.length} ${registerState?.variants?.length === 1 ? t("Variant") : t("Variants")
              }`}</span>
          </div>
          <div className="flex-1 overflow-auto h-full">
            {registerState?.variants?.map(variant => {
              const selectedQuantity = selectedVariantQuantities[variant.id];
              const isSelected = selectedQuantity != null;
              const quantityAvailable = getQuantityAvailableForSelectedRegister(variant);

              return (
                <div
                  key={String(variant.id)}
                  className={classNames("ui-register-product-variant-container", {
                    "ui-register-product-variant-disabled": variant?.meta?.cannot_be_sold,
                    "ui-register-product-variant-container-selected": isVariantMultiselectEnabled && isSelected,
                  })}
                  onClick={
                    !variant?.meta?.cannot_be_sold
                      ? () =>
                        isVariantMultiselectEnabled
                          ? toggleVariantMultiselect(variant)
                          : checkModifiers(variant, registerState?.selectedModifierProduct)
                      : undefined
                  }
                >
                  <div className="flex w-full flex-col">
                    <span className="ui-register-product-variant-container-title">{variant.title}</span>
                    <span className="ui-register-product-variant-container-sub_title">{`${registerState?.selectedModifierProduct?.track_inventory && quantityAvailable
                      ? `${String(quantityAvailable)} in stock`
                      : ""
                      }`}
                    </span>
                  </div>

                  <div className="mr-6 ui-register-product-variant-container-price_title">
                    <LocaleCurrency currency={cart?.currency} amount={variant.price} />
                  </div>
                  {isVariantMultiselectEnabled ? (
                    isSelected ? (
                      <div
                        className="ui-register-product-variant-qty"
                        onClick={event => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="ui-register-product-variant-qty-btn"
                          onClick={() => updateSelectedVariantQuantity(variant.id, -1)}
                          aria-label={t("Decrease quantity")}
                        >
                          <FontAwesomeIcon icon={faMinus} />
                        </button>
                        <span className="ui-register-product-variant-qty-value">{selectedQuantity}</span>
                        <button
                          type="button"
                          className="ui-register-product-variant-qty-btn"
                          onClick={() => updateSelectedVariantQuantity(variant.id, 1)}
                          aria-label={t("Increase quantity")}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      </div>
                    ) : (
                      <FontAwesomeIcon icon={faPlus} className="ui-register-product-variant-icon" />
                    )
                  ) : (
                    <FontAwesomeIcon icon={faCartPlus} className="ui-register-product-variant-icon" />
                  )}
                </div>
              );
            })}
          </div>
        </Sheet>
      </Portal>

      {/* Details modal */}
      <Portal isMounted={state.isOpen}>
        <Sheet
          title={`${state.product?.title} Details`}
          open={state.isOpen}
          onCancel={closeModal}
          closable
          hideCancelButton
          size="small"
          stacked
        >
          {!state.product ? null : (
            <>
              {state.product.track_inventory ? (
                // Inventory is tracked.  Column added to end of table
                <DataTable
                  columns={
                    state.product.variant_count > 1
                      ? [
                        { label: "" }, // variant.title
                        { label: t("secure.facility.register.register_new.products_new.014"), justify: "right" },
                        { label: t("secure.facility.register.register_new.products_new.015"), justify: "right" },
                        { label: t("secure.facility.register.register_new.products_new.016"), justify: "right" },
                      ]
                      : [
                        { label: t("secure.facility.register.register_new.products_new.014"), justify: "right" },
                        { label: t("secure.facility.register.register_new.products_new.015"), justify: "right" },
                        { label: t("secure.facility.register.register_new.products_new.016"), justify: "right" },
                      ]
                  }
                >
                  {state.product.variants.map(variant => (
                    <tr key={variant.id}>
                      {state.product.variant_count > 1 ? <td>{variant.title}</td> : null}
                      <td align="right">
                        <LocaleCurrency currency={cart?.currency ?? "cad"} amount={variant.cost} />
                      </td>
                      <td align="right">
                        <LocaleCurrency currency={cart?.currency ?? "cad"} amount={variant.price} />
                      </td>
                      <td align="right">{getQuantityAvailableForSelectedRegister(variant)}</td>
                    </tr>
                  ))}
                </DataTable>
              ) : (
                // NO Inventory count column
                <DataTable
                  columns={
                    state.product.variant_count > 1
                      ? [
                        { label: "" }, // variant.title
                        { label: t("secure.facility.register.register_new.products_new.014"), justify: "right" },
                        { label: t("secure.facility.register.register_new.products_new.015"), justify: "right" },
                      ]
                      : [
                        { label: t("secure.facility.register.register_new.products_new.014"), justify: "right" },
                        { label: t("secure.facility.register.register_new.products_new.015"), justify: "right" },
                      ]
                  }
                >
                  {state.product.variants.map(variant => (
                    <tr key={variant.id}>
                      {state.product.variant_count > 1 ? <td>{variant.title}</td> : null}
                      <td align="right">
                        <LocaleCurrency currency={cart?.currency ?? "cad"} amount={variant.cost} />
                      </td>
                      <td align="right">
                        <LocaleCurrency currency={cart?.currency ?? "cad"} amount={variant.price} />
                      </td>
                    </tr>
                  ))}
                </DataTable>
              )}
            </>
          )}
        </Sheet>
      </Portal>

      <SelectModifierSheet
        open={modifierState.sheetOpen}
        onCancel={handleCloseModifierGroups}
        onOk={onModifiersOk}
        onClear={clearModifierSelections}
        okDisabled={disableSelectingModifiers()}
        allModifierGroups={modifierState.allModifierGroups}
        selectedModifierGroupVariants={modifierState.selectedModifierVariants}
        addModifierVariant={addModifierVariant}
        changeModifierQuantity={changeModifierQuantity}
        allowModifierQuantity={
          registerState?.selectedModifierProduct?.type === "Group Membership" ||
          registerState?.modifierLineItem?.product?.type === "Group Membership"
        }
        currency={cart?.currency}
        productTitle={
          registerState?.selectedModifierProduct?.preferred_title ||
          registerState?.selectedModifierProduct?.title ||
          registerState?.modifierLineItem?.preferred_title ||
          registerState?.modifierLineItem?.product?.preferred_title ||
          registerState?.modifierLineItem?.product?.title ||
          registerState?.modifierLineItem?.product_title
        }
        isEditing={!!registerState?.modifierLineItem}
      />

      <SelectDynamicComboSheet
        open={dynamicComboState.sheetOpen}
        onCancel={handleCloseDynamicComboSheet}
        onOk={onDynamicComboOk}
        dynamicCombo={registerState?.selectedDynamicComboProduct}
        dynamicComboVariant={registerState?.selectedDynamicComboVariant}
        dynamicComboLineItem={registerState?.dynamicComboLineItem}
      />
    </div>
  );
}

function SingleProductRender(props: {
  product: IProduct;
  disabled: boolean;
  t: TFunction;
  onContentClick: (product: IProduct, sourceElement: HTMLElement | null) => void;
  onMenuClick: (product: IProduct) => void;
  cart: ICart;
  teeSheetSettings: IPutTeeSheetSettings;
}) {
  const { product, disabled, onContentClick, cart, t, teeSheetSettings } = props;
  const backgroundColorBrightness = product.meta?.button_color ? calculateHexColorBrightness(product.meta.button_color) : null;
  const foregroundColor =
    backgroundColorBrightness === null ? "white" : backgroundColorBrightness < 50 ? "white" : "black";

  const textBgStyle = (product?.default_image?.source && teeSheetSettings?.show_product_images)
    ? { backgroundColor: "rgba(0, 0, 0, 0.8)", borderRadius: "4px", padding: "2px 4px" }
    : {};

  return (
    <RightClickMenu
      sections={[
        [
          {
            icon: "circle-info",
            title: t("secure.facility.register.register_new.products_new.017"),
            action: () => props.onMenuClick(product),
            // disabled: String(segment.blocked_type) === "open",
          },
        ],
      ]}
    >
      <div className="product_container">
        <div
          className={"product_content " + (disabled || product?.meta?.cannot_be_sold ? "product_content-disabled" : "")}
          style={
            (product?.default_image?.source && teeSheetSettings?.show_product_images)
              ? {
                backgroundImage: `url(${product.default_image.source})`,
                backgroundSize: "100% auto",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }
              : product.meta?.button_color
                ? { background: product?.meta?.button_color, color: foregroundColor }
                : {}
          }
          onClick={
            disabled || product?.meta?.cannot_be_sold
              ? undefined
              : event => {
                const sourceElement = event.currentTarget.closest(".product_container");
                onContentClick(
                  product,
                  sourceElement instanceof HTMLElement ? sourceElement : null,
                );
              }
          }
        >
          <div style={textBgStyle}>
            <div className="product-title">{product.preferred_title ? product.preferred_title : product.title}</div>
            {product.subtitle ? <div className="product-vendor">{product.subtitle}</div> : null}
            {teeSheetSettings?.product_card_display_vendor ? (<div className="product-vendor">{product.vendor_title}</div>) : null}
          </div>
          <div className="product-details" style={product?.variant_count === 1 && product?.variants[0]?.inventory_item?.tracked && teeSheetSettings?.product_card_display_inventory ? textBgStyle : undefined}>
            <div className="product-stock">
              {product?.variant_count === 1 && product?.variants[0]?.inventory_item?.tracked && teeSheetSettings?.product_card_display_inventory && !product?.warehouse_product_id
                ? getQuantityAvailableForSelectedRegister(product.variants[0])
                : null}
            </div>
            <div className="product-price" style={!(product?.variant_count === 1 && product?.variants[0]?.inventory_item?.tracked && teeSheetSettings?.product_card_display_inventory) && product.variants?.length === 1 ? textBgStyle : undefined}>
              {product.variants?.length === 1 ? (
                <LocaleCurrency currency={cart?.currency} amount={product?.variants[0]?.price?.toFixed(2)} />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
    </RightClickMenu>
  );
}
